import { Part, FlangeId, EdgeKey, HoleId } from './types';
import { bendAllowance } from './bendMath';
import { bendReliefDimensions } from './validate';
import { findActiveMiterPairs } from './miterPairs';
import { getKFactor } from './materials';

export type ReliefOpenSide = 'left' | 'right' | 'top' | 'bottom';

export interface FlatRect {
  kind: 'flange' | 'bend' | 'relief';
  ownerId: FlangeId;        // flange id (for flange) or child flange id (for bend/relief)
  parentId?: FlangeId;
  x: number;                // mm, lower-left in flat layout
  y: number;
  w: number;
  h: number;
  // For reliefs: which side faces the bend (and therefore the parent edge / bend tangent).
  // Used to convert the rectangular notch into an obround when post-processing the outline.
  openSide?: ReliefOpenSide;
}

// A hole projected onto the flat pattern. Coordinates are in flat space (mm).
// `rotation` is in radians, oriented along flat +x = 0.
export interface FlatHole {
  holeId: HoleId;
  flangeId: FlangeId;
  kind: 'round' | 'slot';
  x: number;
  y: number;
  diameter: number;
  length: number;       // slot length (== diameter for round)
  rotation: number;     // radians (slot orientation in flat space)
}

export interface FlatLayout {
  rects: FlatRect[];
  bbox: { x: number; y: number; w: number; h: number };
  miterCuts: MiterCut[];
  holes: FlatHole[];
}

export interface MiterCut {
  flangeId: FlangeId;
  // Axis-aligned ranges to subtract from the CUT layer of the outline (top + far edges
  // of the flange rect that get removed by the cut).
  removeFromCut: Array<{ x1: number; y1: number; x2: number; y2: number }>;
  // Axis-aligned range to subtract from the BEND tangent (only present when gap > 0:
  // the small piece of bend tangent at the corner that is no longer flange-adjacent).
  removeFromBend: Array<{ x1: number; y1: number; x2: number; y2: number }>;
  // CUT segments to add: the 45° miter line plus, when gap > 0, the small "fillet"
  // segment along what used to be the bend tangent.
  addCut: Array<{ x1: number; y1: number; x2: number; y2: number }>;
}

interface FlatTransform {
  ox: number; oy: number;       // flat position of flange's local (0,0)
  pXx: number; pXy: number;     // flat direction of flange's local +X (unit, axis-aligned)
  pYx: number; pYy: number;     // flat direction of flange's local +Y
}

function applyT(t: FlatTransform, lx: number, ly: number) {
  return {
    x: t.ox + t.pXx * lx + t.pYx * ly,
    y: t.oy + t.pXy * lx + t.pYy * ly,
  };
}

function rectOf(t: FlatTransform, length: number, width: number) {
  const c = [
    applyT(t, 0, 0),
    applyT(t, length, 0),
    applyT(t, 0, width),
    applyT(t, length, width),
  ];
  const xs = c.map((p) => p.x);
  const ys = c.map((p) => p.y);
  const minX = Math.min(...xs), minY = Math.min(...ys);
  const maxX = Math.max(...xs), maxY = Math.max(...ys);
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function edgeFrameLocal(edge: EdgeKey, length: number, width: number) {
  switch (edge) {
    case '+x': return { ex: length, ey: 0, ux: 1, uy: 0, vx: 0, vy: 1 };
    case '-x': return { ex: 0, ey: 0, ux: -1, uy: 0, vx: 0, vy: 1 };
    case '+y': return { ex: 0, ey: width, ux: 0, uy: 1, vx: 1, vy: 0 };
    case '-y': return { ex: 0, ey: 0, ux: 0, uy: -1, vx: 1, vy: 0 };
  }
}

export function unfold(part: Part): FlatLayout {
  const rects: FlatRect[] = [];
  const xforms: Record<FlangeId, FlatTransform> = {};

  const root = part.flanges[part.rootId];
  if (!root) return { rects, bbox: { x: 0, y: 0, w: 0, h: 0 }, miterCuts: [], holes: [] };

  xforms[part.rootId] = { ox: 0, oy: 0, pXx: 1, pXy: 0, pYx: 0, pYy: 1 };
  const rootRect = rectOf(xforms[part.rootId], root.length, root.width);
  rects.push({ kind: 'flange', ownerId: part.rootId, ...rootRect });

  const walk = (id: FlangeId) => {
    const f = part.flanges[id];
    if (!f) return;
    const pT = xforms[id];

    for (const childId of f.children) {
      const child = part.flanges[childId];
      if (!child || !child.attachment) continue;

      // Per-bend K-factor from the standard R/T table (more accurate than a
      // single global K, since each bend can have a different radius).
      const k = getKFactor(part.material, child.attachment.bendRadius, part.thickness);
      const BA = bendAllowance(
        child.attachment.bendAngle,
        child.attachment.bendRadius,
        part.thickness,
        k,
      );

      const ef = edgeFrameLocal(child.attachment.edge, f.length, f.width);
      const offset = child.attachment.offset;

      // Bend region: rectangle in PARENT LOCAL coords
      // - corners in parent local: (ex + offset*vx, ey + offset*vy) to (...) + BA*u + child.width * v
      const bendTLocal = {
        startX: ef.ex + offset * ef.vx,
        startY: ef.ey + offset * ef.vy,
        ux: ef.ux, uy: ef.uy,
        vx: ef.vx, vy: ef.vy,
      };
      const bendT: FlatTransform = {
        ox: pT.ox + pT.pXx * bendTLocal.startX + pT.pYx * bendTLocal.startY,
        oy: pT.oy + pT.pXy * bendTLocal.startX + pT.pYy * bendTLocal.startY,
        pXx: pT.pXx * ef.ux + pT.pYx * ef.uy,
        pXy: pT.pXy * ef.ux + pT.pYy * ef.uy,
        pYx: pT.pXx * ef.vx + pT.pYx * ef.vy,
        pYy: pT.pXy * ef.vx + pT.pYy * ef.vy,
      };
      const bendRect = rectOf(bendT, BA, child.width);
      if (BA > 1e-6) {
        rects.push({ kind: 'bend', ownerId: childId, parentId: id, ...bendRect });
      }

      // Bend reliefs: notches cut INTO the parent flange at each end of the bend
      // when the bend doesn't span the full parent edge. Per SendCutSend rules:
      // depth = T + R + 0.5 mm (along outward direction, into parent)
      // width = max(T/2, 1 mm) (along edge direction, outside the bend region)
      const edgeLen = child.attachment.edge === '+x' || child.attachment.edge === '-x'
        ? f.width : f.length;
      const reliefDims = bendReliefDimensions(part.thickness, child.attachment.bendRadius);
      const lowSpace = child.attachment.offset;        // parent material on -v side of bend
      const highSpace = edgeLen - (child.attachment.offset + child.width); // +v side
      const reliefDepth = reliefDims.depth;
      const reliefWidth = reliefDims.width;

      // Open side of the relief faces the bend (i.e. +u in bend local).
      // Map +u via bendT to a flat axis-aligned direction, then pick the rect side it points to.
      let openSide: ReliefOpenSide;
      if (Math.abs(bendT.pXx) > 0.5) openSide = bendT.pXx > 0 ? 'right' : 'left';
      else openSide = bendT.pXy > 0 ? 'top' : 'bottom';

      const addRelief = (uLo: number, uHi: number, vLo: number, vHi: number) => {
        // bendT maps (u, v) where u is outward (toward child) along parent edge perpendicular,
        // v is along the edge. For a relief INTO the parent we use uLo=-depth, uHi=0 and v on the
        // appropriate side outside the bend region. We map 4 corners and take bbox.
        const c = [
          applyT(bendT, uLo, vLo),
          applyT(bendT, uHi, vLo),
          applyT(bendT, uLo, vHi),
          applyT(bendT, uHi, vHi),
        ];
        const xs = c.map((p) => p.x), ys = c.map((p) => p.y);
        rects.push({
          kind: 'relief',
          ownerId: childId,
          parentId: id,
          x: Math.min(...xs),
          y: Math.min(...ys),
          w: Math.max(...xs) - Math.min(...xs),
          h: Math.max(...ys) - Math.min(...ys),
          openSide,
        });
      };
      if (lowSpace > 1e-3) {
        addRelief(-reliefDepth, 0, -reliefWidth, 0);
      }
      if (highSpace > 1e-3) {
        addRelief(-reliefDepth, 0, child.width, child.width + reliefWidth);
      }

      // Child flange transform: starts at end of bend region (BA further in u direction)
      const childT: FlatTransform = {
        ox: bendT.ox + bendT.pXx * BA,
        oy: bendT.oy + bendT.pXy * BA,
        pXx: bendT.pXx, pXy: bendT.pXy,
        pYx: bendT.pYx, pYy: bendT.pYy,
      };
      xforms[childId] = childT;
      const childRect = rectOf(childT, child.length, child.width);
      rects.push({ kind: 'flange', ownerId: childId, ...childRect });

      walk(childId);
    }
  };

  walk(part.rootId);

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const r of rects) {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.w);
    maxY = Math.max(maxY, r.y + r.h);
  }
  if (!isFinite(minX)) { minX = minY = 0; maxX = maxY = 0; }

  const miterCuts = detectMiterCuts(part, xforms);

  // Project holes from flange-local to flat coords using each flange's flat transform.
  const holes: FlatHole[] = [];
  for (const h of part.holes) {
    const xf = xforms[h.flangeId];
    if (!xf) continue;
    const flat = applyT(xf, h.x, h.y);
    // Compose flange-local rotation with flange's flat orientation. The slot's local
    // direction (cos rot, sin rot) maps to flat as xAxis*cos + yAxis*sin.
    const localRot = ((h.rotation ?? 0) * Math.PI) / 180;
    const dirX = xf.pXx * Math.cos(localRot) + xf.pYx * Math.sin(localRot);
    const dirY = xf.pXy * Math.cos(localRot) + xf.pYy * Math.sin(localRot);
    const flatRot = Math.atan2(dirY, dirX);
    holes.push({
      holeId: h.id,
      flangeId: h.flangeId,
      kind: h.kind,
      x: flat.x,
      y: flat.y,
      diameter: h.diameter,
      length: Math.max(h.length ?? h.diameter, h.diameter),
      rotation: flatRot,
    });
  }
  return { rects, bbox: { x: minX, y: minY, w: maxX - minX, h: maxY - minY }, miterCuts, holes };
}

function detectMiterCuts(part: Part, xforms: Record<FlangeId, FlatTransform>): MiterCut[] {
  const cuts: MiterCut[] = [];
  const gap = Math.max(0, part.miterGap ?? 0);
  // 3D-aware pair detection — handles siblings on perpendicular edges AND
  // grandchildren whose caps meet at a corner of the box's top.
  const pairs = findActiveMiterPairs(part);
  for (const pair of pairs) {
    const flangeA = part.flanges[pair.a.flangeId];
    const flangeB = part.flanges[pair.b.flangeId];
    if (!flangeA || !flangeB) continue;
    const cutA = computeFlangeMiter(flangeA, xforms[flangeA.id], pair.a.end, gap);
    const cutB = computeFlangeMiter(flangeB, xforms[flangeB.id], pair.b.end, gap);
    if (cutA) cuts.push(cutA);
    if (cutB) cuts.push(cutB);
  }
  return cuts;
}

function computeFlangeMiter(
  child: { id: FlangeId; length: number; width: number },
  xform: FlatTransform | undefined,
  end: 'low' | 'high',
  gap: number,
): MiterCut | null {
  if (!xform) return null;
  const length = child.length;
  const width = child.width;
  // Full miter requires the cut to land on the FAR edge — only valid when length ≤ width.
  if (length > width + 1e-3) return null;
  // Gap must leave enough room for the diagonal to still hit the far edge above 0.
  if (gap > 0 && gap > width - length - 1e-6) return null;

  // Cut endpoints in LOCAL frame, shifted inward by gap along the bend-tangent / far-edge axes.
  // For 'high' end: shift in -y; for 'low' end: shift in +y. x-axis is unaffected.
  const yEdgeOriginal = end === 'high' ? width : 0;        // the un-gapped near corner y
  const yEdge = end === 'high' ? width - gap : gap;        // shifted cut start on bend tangent
  const yFar = end === 'high' ? width - length - gap : length + gap;  // shifted cut end on far edge

  const flat = (lx: number, ly: number) => ({
    x: xform.ox + xform.pXx * lx + xform.pYx * ly,
    y: xform.oy + xform.pXy * lx + xform.pYy * ly,
  });

  const TopCorner = flat(0, yEdgeOriginal);   // original near corner (bend tangent ∩ original near edge)
  const A1 = flat(0, yEdge);                   // shifted cut start (still on bend tangent line)
  const A2 = flat(length, yEdgeOriginal);      // far-end corner of the rect being cut off
  const B2 = flat(length, yFar);               // shifted cut end (on far edge)

  const removeFromCut: MiterCut['removeFromCut'] = [
    // Original near edge (TOP for 'high', BOTTOM for 'low'): full length, removed.
    { x1: TopCorner.x, y1: TopCorner.y, x2: A2.x, y2: A2.y },
    // FAR edge segment from far-end corner to gap-shifted cut endpoint (length = leg + gap).
    { x1: A2.x, y1: A2.y, x2: B2.x, y2: B2.y },
  ];

  const removeFromBend: MiterCut['removeFromBend'] = [];
  const addCut: MiterCut['addCut'] = [
    // The 45° miter line.
    { x1: A1.x, y1: A1.y, x2: B2.x, y2: B2.y },
  ];

  if (gap > 1e-6) {
    // The small bit of bend-tangent line between the gapped cut start and the original
    // near corner is no longer flange-adjacent → BEND tangent shortens, replaced by CUT.
    removeFromBend.push({ x1: A1.x, y1: A1.y, x2: TopCorner.x, y2: TopCorner.y });
    addCut.push({ x1: A1.x, y1: A1.y, x2: TopCorner.x, y2: TopCorner.y });
  }

  return { flangeId: child.id, removeFromCut, removeFromBend, addCut };
}

function applyMiterCuts(layout: FlatLayout, segments: OutlineSegment[]): OutlineSegment[] {
  const cuts = layout.miterCuts;
  if (!cuts || cuts.length === 0) return segments;
  const eps = 1e-3;

  // Build per-layer interval-subtraction maps keyed by axis line.
  type RangeMap = Map<string, Array<[number, number]>>;
  const buildMap = (ranges: Array<{ x1: number; y1: number; x2: number; y2: number }>): RangeMap => {
    const m: RangeMap = new Map();
    for (const rm of ranges) {
      const horiz = Math.abs(rm.y1 - rm.y2) < eps;
      const k = horiz ? `h:${rm.y1.toFixed(3)}` : `v:${rm.x1.toFixed(3)}`;
      const lo = horiz ? Math.min(rm.x1, rm.x2) : Math.min(rm.y1, rm.y2);
      const hi = horiz ? Math.max(rm.x1, rm.x2) : Math.max(rm.y1, rm.y2);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push([lo, hi]);
    }
    return m;
  };

  const cutRemoveMap = buildMap(cuts.flatMap((c) => c.removeFromCut));
  const bendRemoveMap = buildMap(cuts.flatMap((c) => c.removeFromBend));

  const subtractInPlace = (
    seg: OutlineSegment,
    map: RangeMap,
    out: OutlineSegment[],
  ): boolean => {
    if (seg.arc) return false;
    const horiz = Math.abs(seg.y1 - seg.y2) < eps;
    const key = horiz ? `h:${seg.y1.toFixed(3)}` : `v:${seg.x1.toFixed(3)}`;
    const removes = map.get(key);
    if (!removes || removes.length === 0) return false;

    const sLo = horiz ? Math.min(seg.x1, seg.x2) : Math.min(seg.y1, seg.y2);
    const sHi = horiz ? Math.max(seg.x1, seg.x2) : Math.max(seg.y1, seg.y2);
    let pieces: Array<[number, number]> = [[sLo, sHi]];
    for (const [rLo, rHi] of removes) {
      const next: Array<[number, number]> = [];
      for (const [lo, hi] of pieces) {
        const oLo = Math.max(lo, rLo);
        const oHi = Math.min(hi, rHi);
        if (oLo >= oHi - eps) { next.push([lo, hi]); continue; }
        if (lo < oLo - eps) next.push([lo, oLo]);
        if (oHi < hi - eps) next.push([oHi, hi]);
      }
      pieces = next;
    }
    // If nothing was actually removed (no overlap), let the caller keep the original segment.
    if (pieces.length === 1 && Math.abs(pieces[0][0] - sLo) < eps && Math.abs(pieces[0][1] - sHi) < eps) {
      return false;
    }
    for (const [lo, hi] of pieces) {
      if (hi - lo < eps) continue;
      if (horiz) out.push({ layer: seg.layer, x1: lo, y1: seg.y1, x2: hi, y2: seg.y2 });
      else out.push({ layer: seg.layer, x1: seg.x1, y1: lo, x2: seg.x2, y2: hi });
    }
    return true;
  };

  const result: OutlineSegment[] = [];
  for (const seg of segments) {
    if (seg.arc) { result.push(seg); continue; }
    const map = seg.layer === 'CUT' ? cutRemoveMap : bendRemoveMap;
    if (!subtractInPlace(seg, map, result)) result.push(seg);
  }
  for (const c of cuts) {
    for (const a of c.addCut) {
      result.push({ layer: 'CUT', x1: a.x1, y1: a.y1, x2: a.x2, y2: a.y2 });
    }
  }
  return result;
}

// Total flat blank area in mm² (flange + bend regions, minus relief notches).
export function flatArea(layout: FlatLayout): number {
  let area = 0;
  for (const r of layout.rects) {
    const a = r.w * r.h;
    area += r.kind === 'relief' ? -a : a;
  }
  return Math.max(0, area);
}

// Estimated mass in kg given flat area, thickness, and density (g/cm³).
export function estimatedMass(layout: FlatLayout, thickness: number, densityGcm3: number): number {
  const volMm3 = flatArea(layout) * thickness;
  return (volMm3 * densityGcm3) / 1e6; // kg
}

// A unified segment type. `arc` is set for arc segments; lines have it undefined.
// Endpoints (x1,y1)→(x2,y2) are the start/end of the curve, oriented as drawn.
export interface OutlineSegment {
  layer: 'CUT' | 'BEND';
  x1: number; y1: number; x2: number; y2: number;
  arc?: {
    cx: number; cy: number;
    radius: number;
    // True if the arc sweeps CCW (math convention, +y up) from (x1,y1) to (x2,y2).
    ccw: boolean;
  };
}

export interface BendCenterLine { x1: number; y1: number; x2: number; y2: number; }

// Centerline of each bend region — a line through the middle of the bend rect along
// its long axis. Used as a visual marker for the press-brake operator.
//
// One centerline per distinct bend rect, sitting at the geometric center of that
// rect. Step patterns where the chain folds back onto itself (e.g., +90/+90)
// produce bend rects at the SAME flat-axis position; we dedupe those exact overlaps
// (within `tol`) so the DXF doesn't carry a duplicate LINE entity, but bends at
// distinct axis positions — including chained flanges and Z-bends — stay separate
// so each press-brake operation gets its own visible mark.
export function computeBendCenterLines(layout: FlatLayout): BendCenterLine[] {
  const tol = 1e-3;
  type Raw = { isHoriz: boolean; axis: number; lo: number; hi: number };
  const raw: Raw[] = layout.rects
    .filter((r) => r.kind === 'bend')
    .map((r) => {
      const isHoriz = r.w >= r.h;
      return isHoriz
        ? { isHoriz, axis: r.y + r.h / 2, lo: r.x, hi: r.x + r.w }
        : { isHoriz, axis: r.x + r.w / 2, lo: r.y, hi: r.y + r.h };
    });

  const merged: Raw[] = [];
  for (const line of raw) {
    const idx = merged.findIndex(
      (m) => m.isHoriz === line.isHoriz && Math.abs(m.axis - line.axis) < tol,
    );
    if (idx === -1) merged.push({ ...line });
    else {
      merged[idx].lo = Math.min(merged[idx].lo, line.lo);
      merged[idx].hi = Math.max(merged[idx].hi, line.hi);
    }
  }

  return merged.map((m) =>
    m.isHoriz
      ? { x1: m.lo, y1: m.axis, x2: m.hi, y2: m.axis }
      : { x1: m.axis, y1: m.lo, x2: m.axis, y2: m.hi },
  );
}

// Compute outline (CUT) and bend lines (BEND) using axis-aligned edge cancellation.
// At each axis level (y= for horizontal edges, x= for vertical), collect intervals each with
// direction +1 (rect interior on the +axis side) or -1 (interior on -axis side). Where the
// running sum is nonzero, we're on the outline. Where it cancels to zero AND a bend rect
// is present, we have a bend line.
export function computeOutline(layout: FlatLayout): OutlineSegment[] {
  type Contrib = { lo: number; hi: number; dir: 1 | -1; kind: FlatRect['kind'] };
  const horiz = new Map<number, Contrib[]>();   // key: y-level
  const vert = new Map<number, Contrib[]>();    // key: x-level
  const k = (n: number) => Math.round(n * 1000) / 1000;

  for (const r of layout.rects) {
    const x1 = r.x, x2 = r.x + r.w, y1 = r.y, y2 = r.y + r.h;
    if (x2 - x1 < 1e-6 || y2 - y1 < 1e-6) continue;
    // Reliefs are CUTOUTS — their winding is reversed (CW), so direction signs flip.
    const sign: 1 | -1 = r.kind === 'relief' ? -1 : 1;
    const yBot = k(y1), yTop = k(y2), xLft = k(x1), xRt = k(x2);
    if (!horiz.has(yBot)) horiz.set(yBot, []);
    horiz.get(yBot)!.push({ lo: x1, hi: x2, dir: (1 * sign) as 1 | -1, kind: r.kind });
    if (!horiz.has(yTop)) horiz.set(yTop, []);
    horiz.get(yTop)!.push({ lo: x1, hi: x2, dir: (-1 * sign) as 1 | -1, kind: r.kind });
    if (!vert.has(xLft)) vert.set(xLft, []);
    vert.get(xLft)!.push({ lo: y1, hi: y2, dir: (1 * sign) as 1 | -1, kind: r.kind });
    if (!vert.has(xRt)) vert.set(xRt, []);
    vert.get(xRt)!.push({ lo: y1, hi: y2, dir: (-1 * sign) as 1 | -1, kind: r.kind });
  }

  const out: OutlineSegment[] = [];

  const sweep = (
    map: Map<number, Contrib[]>,
    isHorizontal: boolean,
  ) => {
    for (const [level, contribs] of map.entries()) {
      const points = new Set<number>();
      for (const c of contribs) { points.add(c.lo); points.add(c.hi); }
      const sorted = Array.from(points).sort((a, b) => a - b);
      // Combine adjacent same-status intervals
      let runStart: number | null = null;
      let runStatus: 'CUT' | 'BEND' | null = null;
      const flush = (end: number) => {
        if (runStart === null || runStatus === null) return;
        if (isHorizontal) out.push({ layer: runStatus, x1: runStart, y1: level, x2: end, y2: level });
        else out.push({ layer: runStatus, x1: level, y1: runStart, x2: level, y2: end });
        runStart = null; runStatus = null;
      };
      for (let i = 0; i < sorted.length - 1; i++) {
        const a = sorted[i], b = sorted[i + 1];
        if (b - a < 1e-6) continue;
        const mid = (a + b) / 2;
        let sum = 0, hasBend = false, hasFlange = false;
        for (const c of contribs) {
          if (c.lo <= mid + 1e-6 && c.hi >= mid - 1e-6) {
            sum += c.dir;
            if (c.kind === 'bend') hasBend = true;
            else if (c.kind === 'flange') hasFlange = true;
          }
        }
        // Bend tangent line: a flange and a bend share this edge, both fully inside the union.
        const status: 'CUT' | 'BEND' | null =
          sum !== 0 ? 'CUT' : (hasBend && hasFlange ? 'BEND' : null);
        if (status === runStatus && runStart !== null) {
          // continue run
        } else {
          flush(a);
          if (status !== null) {
            runStart = a;
            runStatus = status;
          }
        }
      }
      flush(sorted[sorted.length - 1] ?? 0);
    }
  };

  sweep(horiz, true);
  sweep(vert, false);

  return applyMiterCuts(layout, applyObroundReliefs(layout, out));
}

// Replace each rectangular relief notch in the outline with an obround (rounded slot)
// per SendCutSend recommendation. The 3 closed rectangle sides become 2 shoulder lines
// + 1 semicircular arc.
function applyObroundReliefs(layout: FlatLayout, segments: OutlineSegment[]): OutlineSegment[] {
  const reliefs = layout.rects.filter((r) => r.kind === 'relief' && r.openSide);
  if (reliefs.length === 0) return segments;

  const eps = 1e-3;

  // For each relief, derive its 3 closed sides (cut edges) and obround replacement.
  type ReliefMeta = {
    r: FlatRect;
    // Closed sides as line segments (x1,y1,x2,y2 with min,max ordering)
    sides: { axis: 'x' | 'y'; level: number; lo: number; hi: number }[];
    replacement: OutlineSegment[];
  };

  const meta: ReliefMeta[] = reliefs.map((r) => {
    const x1 = r.x, x2 = r.x + r.w, y1 = r.y, y2 = r.y + r.h;
    const open = r.openSide!;
    let depthLevel: number, openLevel: number;       // axis perpendicular to open side
    let depthAxis: 'x' | 'y';                         // axis perpendicular to open side
    let span1: number, span2: number;                 // span across open side
    let spanAxis: 'x' | 'y';                          // axis along open side
    if (open === 'right') { depthAxis = 'x'; openLevel = x2; depthLevel = x1; spanAxis = 'y'; span1 = y1; span2 = y2; }
    else if (open === 'left') { depthAxis = 'x'; openLevel = x1; depthLevel = x2; spanAxis = 'y'; span1 = y1; span2 = y2; }
    else if (open === 'top') { depthAxis = 'y'; openLevel = y2; depthLevel = y1; spanAxis = 'x'; span1 = x1; span2 = x2; }
    else { depthAxis = 'y'; openLevel = y1; depthLevel = y2; spanAxis = 'x'; span1 = x1; span2 = x2; }

    const W = span2 - span1;
    const D = Math.abs(openLevel - depthLevel);
    const inwardSign = openLevel > depthLevel ? -1 : 1; // direction from open toward deep along depthAxis

    // 3 closed sides, expressed as horizontal/vertical line segments
    const sides: ReliefMeta['sides'] = [];
    // shoulder1: along depthAxis at spanAxis = span1, from openLevel to depthLevel
    sides.push({ axis: depthAxis, level: span1, lo: Math.min(openLevel, depthLevel), hi: Math.max(openLevel, depthLevel) });
    // shoulder2: along depthAxis at spanAxis = span2
    sides.push({ axis: depthAxis, level: span2, lo: Math.min(openLevel, depthLevel), hi: Math.max(openLevel, depthLevel) });
    // deep: along spanAxis at depthAxis = depthLevel
    sides.push({ axis: spanAxis, level: depthLevel, lo: span1, hi: span2 });

    // Obround replacement, only if D ≥ W/2 (otherwise keep rectangular by leaving sides untouched).
    let replacement: OutlineSegment[] = [];
    if (D >= W / 2 + eps) {
      const radius = W / 2;
      const arcDepthLevel = depthLevel - inwardSign * radius; // where the straight section ends
      const spanMid = (span1 + span2) / 2;
      // Two shoulders (line) + one semicircular arc.
      // Shoulder endpoints (openLevel → arcDepthLevel) at spanLevel
      const mkLine = (s1Span: number, s2Span: number, depth1: number, depth2: number): OutlineSegment => {
        if (depthAxis === 'x') {
          // depth = x, span = y
          return { layer: 'CUT', x1: depth1, y1: s1Span, x2: depth2, y2: s2Span };
        } else {
          return { layer: 'CUT', x1: s1Span, y1: depth1, x2: s2Span, y2: depth2 };
        }
      };
      replacement.push(mkLine(span1, span1, openLevel, arcDepthLevel));
      replacement.push(mkLine(span2, span2, openLevel, arcDepthLevel));
      // Arc from (depthAxis=arcDepthLevel, spanAxis=span1) to (arcDepthLevel, span2),
      // center at (arcDepthLevel, spanMid), radius = W/2, bulging toward the deep side.
      const cx = depthAxis === 'x' ? arcDepthLevel : spanMid;
      const cy = depthAxis === 'x' ? spanMid : arcDepthLevel;
      const ax1 = depthAxis === 'x' ? arcDepthLevel : span1;
      const ay1 = depthAxis === 'x' ? span1 : arcDepthLevel;
      const ax2 = depthAxis === 'x' ? arcDepthLevel : span2;
      const ay2 = depthAxis === 'x' ? span2 : arcDepthLevel;
      // Direction: arc bulges toward deep side. Determine ccw flag (math, +y up) from start→end going through deep apex.
      // Apex of arc is at depthAxis = depthLevel, spanAxis = spanMid (on the deep side of arcDepthLevel).
      // Start at (start), end at (end), passing through apex.
      // Compute via cross product sign of (end - center) × (apex - center).
      const apexX = depthAxis === 'x' ? depthLevel : spanMid;
      const apexY = depthAxis === 'x' ? spanMid : depthLevel;
      const v1x = ax1 - cx, v1y = ay1 - cy;
      const vAx = apexX - cx, vAy = apexY - cy;
      // cross (v1 × vA): sign tells whether apex is CCW from start
      const crossA = v1x * vAy - v1y * vAx;
      const ccw = crossA > 0;
      replacement.push({
        layer: 'CUT',
        x1: ax1, y1: ay1, x2: ax2, y2: ay2,
        arc: { cx, cy, radius, ccw },
      });
    }

    return { r, sides, replacement };
  });

  // Filter out outline segments that lie on any relief's closed sides.
  const result: OutlineSegment[] = [];
  for (const seg of segments) {
    if (seg.arc) { result.push(seg); continue; }
    const isHoriz = Math.abs(seg.y1 - seg.y2) < eps;
    const isVert = Math.abs(seg.x1 - seg.x2) < eps;
    let absorbed = false;
    for (const m of meta) {
      if (m.replacement.length === 0) continue; // keep rectangular for shallow reliefs
      for (const s of m.sides) {
        if (s.axis === 'x') {
          // segment must be vertical (along x is wrong — confusingly: axis='x' means level is fixed in y)
          // s.axis='x' means the side runs ALONG x (horizontal line at y=level)
          if (!isHoriz) continue;
          if (Math.abs(seg.y1 - s.level) > eps) continue;
          const sLo = Math.min(seg.x1, seg.x2), sHi = Math.max(seg.x1, seg.x2);
          if (sLo > s.lo - eps && sHi < s.hi + eps) { absorbed = true; break; }
        } else {
          // s.axis='y' means side runs ALONG y (vertical line at x=level)
          if (!isVert) continue;
          if (Math.abs(seg.x1 - s.level) > eps) continue;
          const sLo = Math.min(seg.y1, seg.y2), sHi = Math.max(seg.y1, seg.y2);
          if (sLo > s.lo - eps && sHi < s.hi + eps) { absorbed = true; break; }
        }
      }
      if (absorbed) break;
    }
    if (!absorbed) result.push(seg);
  }

  for (const m of meta) result.push(...m.replacement);
  return result;
}
